/**
 * Incoming WebHook トリガー スキーマ
 *
 * 外部システムから送信される WebHook を契機にデスクトップでタスクを実行する機能の
 * 境界スキーマ（API I/O・IPC・クラウドレスポンスの SSOT）。
 *
 * - エンドポイント登録/管理: {@link CreateEndpointInputSchema} / {@link WebhookEndpointViewSchema}
 * - 認証設定（判別可能ユニオン）: {@link EndpointAuthSchema}
 * - キュー制御設定（既定値補完）: {@link QueueControlSchema}
 * - 保留トリガー: {@link PendingTriggerSchema} / {@link PendingTriggerViewSchema}
 * - タスク起源メタデータ: {@link WebhookMetadataSchema}
 */
import { z } from 'zod';
import { ModelSelectionSchema } from './base-schemas';
// ========================================
// 基盤 enum
// ========================================
/** エンドポイントの有効・無効状態 */
export const EndpointStatusSchema = z.enum(['enabled', 'disabled']);
/** 実行ポリシー: 全件実行 / 最新のみ */
export const ExecutionPolicySchema = z.enum(['all', 'latest_only']);
/** 未消費上限超過時の挙動: 最古を退避して新着優先 / 新着を拒否 */
export const OverflowPolicySchema = z.enum(['drop_oldest', 'reject_newest']);
// ========================================
// キュー制御設定（既定値補完）
// ========================================
/**
 * エンドポイント単位のキュー制御設定。
 * 各フィールドは個別に既定値を持つため、`parse({})` で全既定値、
 * 部分指定で残りを既定補完する（要件 1.10）。
 */
export const QueueControlSchema = z.object({
    /** 実行ポリシー（既定 'all'） */
    executionPolicy: ExecutionPolicySchema.default('all'),
    /** 実行有効期限（秒）。受信からの経過がこれを超えると期限切れスキップ（既定 86400 = 24時間） */
    stalenessSeconds: z.number().int().positive().default(86400),
    /** 未消費件数上限（既定 100） */
    maxPending: z.number().int().positive().default(100),
    /** 上限超過時の挙動（既定 'drop_oldest'） */
    overflowPolicy: OverflowPolicySchema.default('drop_oldest'),
});
/** 部分更新用のキュー制御設定（各フィールド任意・既定補完なし） */
export const QueueControlPatchSchema = z
    .object({
    executionPolicy: ExecutionPolicySchema,
    stalenessSeconds: z.number().int().positive(),
    maxPending: z.number().int().positive(),
    overflowPolicy: OverflowPolicySchema,
})
    .partial();
// ========================================
// 認証設定（判別可能ユニオン）
// ========================================
/**
 * エンドポイントの送信元認証方式。
 * - hmac: 受信ボディに対する HMAC-SHA256 署名検証（署名ヘッダー名を指定）
 * - token: エンドポイント・トークン照合（ヘッダー or URL パス）。
 *   ヘッダー方式は照合対象のヘッダー名（tokenName）を必須とする。
 */
/** Phase 1 の署名対象テンプレートで許容するトークン（複合テンプレートは Phase 2） */
// 署名対象テンプレートで許容するトークン。
// Phase 1: body/timestamp。Phase 2: method/url（複合: HubSpot/Trello 等）。
const ALLOWED_TEMPLATE_TOKENS = ['body', 'timestamp', 'method', 'url'];
/**
 * HMAC 検証の構成（プロバイダ非依存）。全項目任意・既定値で後方互換（GitHub 互換）。
 * 検証済みプロバイダ仕様は `signature-providers.md` を参照。
 */
// 全項目は任意。未指定時の既定（sha256/hex/utf8/'{body}'/seconds/300）は検証エンジン側で補完する
// （後方互換のため既存の bare な authType もそのまま受け付ける）。
const HmacAuthShape = {
    type: z.literal('hmac'),
    signatureHeader: z.string().min(1),
    /** ハッシュアルゴリズム（既定 sha256） */
    algorithm: z.enum(['sha1', 'sha256']).optional(),
    /** 署名値のエンコーディング（既定 hex） */
    digestEncoding: z.enum(['hex', 'base64']).optional(),
    /** 秘密鍵の前処理。'base64' は鍵を base64 デコードしてバイト列にする（Teams/Chatwork、既定 utf8） */
    keyEncoding: z.enum(['utf8', 'base64']).optional(),
    /** ヘッダ値から除去する接頭辞（例 'sha256=' / 'v0=' / 'v1=' / 'HMAC '） */
    signaturePrefix: z.string().optional(),
    /** 構造化ヘッダから署名を抽出する正規表現（1キャプチャ）。prefix より優先 */
    signaturePattern: z.string().optional(),
    /** 複数署名の区切り（例 ','）。いずれか一致で受理（鍵ローテーション） */
    multiValueSeparator: z.string().optional(),
    /** 署名対象テンプレート。Phase 1 トークン: {body},{timestamp}（既定 '{body}'） */
    signedPayloadTemplate: z.string().optional(),
    /** {timestamp} の供給元ヘッダ（例 'X-Slack-Request-Timestamp'） */
    timestampHeader: z.string().optional(),
    /** 署名ヘッダ内のタイムスタンプ抽出正規表現（Stripe 't=([0-9]+)'） */
    timestampPattern: z.string().optional(),
    /** タイムスタンプの単位（既定 seconds） */
    timestampUnit: z.enum(['seconds', 'milliseconds']).optional(),
    /** リプレイ許容窓（秒、既定 300） */
    toleranceSeconds: z.number().int().positive().optional(),
};
export const EndpointAuthSchema = z
    .discriminatedUnion('type', [
    z.object(HmacAuthShape),
    z.object({
        type: z.literal('token'),
        location: z.enum(['header', 'path']),
        tokenName: z.string().min(1).optional(),
    }),
    // Ed25519 公開鍵署名（Discord）。署名対象は {timestamp}{body}。publicKey は公開情報。
    z.object({
        type: z.literal('ed25519'),
        signatureHeader: z.string().min(1),
        timestampHeader: z.string().min(1),
        publicKey: z.string(),
    }),
    // AWS SNS（X.509/RSA 証明書署名）。署名はボディ内、証明書は SigningCertURL から取得。
    z.object({ type: z.literal('aws_sns') }),
    // PayPal（証明書/RSA 署名）。署名はヘッダ、検証対象は transmissionId|time|webhookId|crc32(body)。
    z.object({ type: z.literal('paypal'), webhookId: z.string() }),
])
    .superRefine((v, ctx) => {
    // token + header 方式は照合対象のヘッダー名（tokenName）を必須とする
    if (v.type === 'token' && v.location === 'header' && !v.tokenName) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'token + header 方式では tokenName（ヘッダー名）が必須です',
            path: ['tokenName'],
        });
    }
    // Phase 1: 署名対象テンプレートは {body}/{timestamp} のみ許容
    if (v.type === 'hmac' && v.signedPayloadTemplate) {
        const tokens = [...v.signedPayloadTemplate.matchAll(/\{([a-zA-Z]+)\}/g)].map((m) => m[1]);
        const invalid = tokens.find((t) => !ALLOWED_TEMPLATE_TOKENS.includes(t));
        if (invalid) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `signedPayloadTemplate の未対応トークン: {${invalid}}（対応: {body}/{timestamp}/{method}/{url}）`,
                path: ['signedPayloadTemplate'],
            });
        }
    }
});
export const WEBHOOK_AUTH_PRESETS = [
    {
        key: 'github',
        label: 'GitHub',
        auth: { type: 'hmac', signatureHeader: 'X-Hub-Signature-256', signaturePrefix: 'sha256=' },
        docsUrl: 'https://docs.github.com/en/webhooks/using-webhooks/validating-webhook-deliveries',
    },
    {
        key: 'shopify',
        label: 'Shopify',
        auth: { type: 'hmac', signatureHeader: 'X-Shopify-Hmac-SHA256', digestEncoding: 'base64' },
    },
    {
        key: 'line',
        label: 'LINE',
        auth: { type: 'hmac', signatureHeader: 'x-line-signature', digestEncoding: 'base64' },
    },
    {
        key: 'line-works',
        label: 'LINE WORKS',
        auth: { type: 'hmac', signatureHeader: 'X-WORKS-Signature', digestEncoding: 'base64' },
    },
    {
        key: 'chatwork',
        label: 'Chatwork',
        auth: {
            type: 'hmac',
            signatureHeader: 'X-ChatWorkWebhookSignature',
            digestEncoding: 'base64',
            keyEncoding: 'base64',
        },
    },
    {
        key: 'typeform',
        label: 'Typeform',
        auth: {
            type: 'hmac',
            signatureHeader: 'Typeform-Signature',
            digestEncoding: 'base64',
            signaturePrefix: 'sha256=',
        },
    },
    {
        key: 'circleci',
        label: 'CircleCI',
        auth: {
            type: 'hmac',
            signatureHeader: 'circleci-signature',
            signaturePrefix: 'v1=',
            multiValueSeparator: ',',
        },
    },
    {
        key: 'pagerduty',
        label: 'PagerDuty',
        auth: {
            type: 'hmac',
            signatureHeader: 'X-PagerDuty-Signature',
            signaturePrefix: 'v1=',
            multiValueSeparator: ',',
        },
    },
    {
        key: 'teams',
        label: 'Microsoft Teams（送信 Webhook）',
        auth: {
            type: 'hmac',
            signatureHeader: 'Authorization',
            digestEncoding: 'base64',
            keyEncoding: 'base64',
            signaturePrefix: 'HMAC ',
        },
    },
    {
        key: 'jira',
        label: 'Jira（secret 設定時）',
        auth: { type: 'hmac', signatureHeader: 'X-Hub-Signature', signaturePrefix: 'sha256=' },
    },
    {
        key: 'salesforce-datacloud',
        label: 'Salesforce Data Cloud',
        auth: { type: 'hmac', signatureHeader: 'x-signature', digestEncoding: 'base64' },
    },
    {
        key: 'slack',
        label: 'Slack',
        auth: {
            type: 'hmac',
            signatureHeader: 'X-Slack-Signature',
            signaturePrefix: 'v0=',
            signedPayloadTemplate: 'v0:{timestamp}:{body}',
            timestampHeader: 'X-Slack-Request-Timestamp',
            toleranceSeconds: 300,
        },
    },
    {
        key: 'zoom',
        label: 'Zoom',
        auth: {
            type: 'hmac',
            signatureHeader: 'x-zm-signature',
            signaturePrefix: 'v0=',
            signedPayloadTemplate: 'v0:{timestamp}:{body}',
            timestampHeader: 'x-zm-request-timestamp',
        },
    },
    {
        key: 'stripe',
        label: 'Stripe',
        auth: {
            type: 'hmac',
            signatureHeader: 'Stripe-Signature',
            signaturePattern: 'v1=([a-f0-9]+)',
            signedPayloadTemplate: '{timestamp}.{body}',
            timestampPattern: 't=([0-9]+)',
            toleranceSeconds: 300,
        },
    },
    {
        key: 'hubspot',
        label: 'HubSpot（v3）',
        auth: {
            type: 'hmac',
            signatureHeader: 'X-HubSpot-Signature-v3',
            digestEncoding: 'base64',
            signedPayloadTemplate: '{method}{url}{body}{timestamp}',
            timestampHeader: 'X-HubSpot-Request-Timestamp',
            timestampUnit: 'milliseconds',
            toleranceSeconds: 300,
        },
    },
    {
        key: 'asana',
        label: 'Asana',
        auth: { type: 'hmac', signatureHeader: 'X-Hook-Signature' },
        establishSecretViaHandshake: true,
    },
    {
        key: 'discord',
        label: 'Discord（Ed25519）',
        auth: {
            type: 'ed25519',
            signatureHeader: 'X-Signature-Ed25519',
            timestampHeader: 'X-Signature-Timestamp',
            publicKey: '',
        },
    },
    {
        key: 'aws-sns',
        label: 'AWS SNS（証明書署名）',
        auth: { type: 'aws_sns' },
    },
    {
        key: 'paypal',
        label: 'PayPal（証明書署名）',
        auth: { type: 'paypal', webhookId: '' },
    },
    {
        key: 'trello',
        label: 'Trello',
        auth: {
            type: 'hmac',
            signatureHeader: 'X-Trello-Webhook',
            algorithm: 'sha1',
            digestEncoding: 'base64',
            signedPayloadTemplate: '{body}{url}',
        },
    },
    {
        key: 'token-header',
        label: '汎用トークン（ヘッダー）',
        auth: { type: 'token', location: 'header', tokenName: 'X-Webhook-Token' },
    },
    {
        key: 'token-path',
        label: '汎用トークン（URL パス）',
        auth: { type: 'token', location: 'path' },
    },
];
// ========================================
// エンドポイント登録・更新入力
// ========================================
/**
 * エンドポイント作成入力。
 * name / workspaceId / promptTemplate / authType を必須とし、
 * 欠如時はバリデーションエラーとなる（要件 1.1, 1.7）。
 * queueControl は未指定時に既定値で補完する（要件 1.9）。
 */
export const CreateEndpointInputSchema = z.object({
    name: z.string().min(1),
    workspaceId: z.string().min(1),
    promptTemplate: z.string().min(1),
    authType: EndpointAuthSchema,
    // zod 4 の .default() は値を再パースせず出力としてそのまま使うため、内側の
    // 既定値を埋める旧挙動を保つには .prefault()（パース前に既定を適用）を使う。
    queueControl: QueueControlSchema.prefault({}),
    /** タスク実行に用いる LLM モデル。未指定（null）ならワークスペースの優先モデルを使う。 */
    selectedModel: ModelSelectionSchema.nullable().optional(),
    /**
     * 署名シークレットを登録ハンドシェイクで確立する場合 true（Asana）。
     * このとき作成時にシークレットは発行せず、最初の X-Hook-Secret 受領で確立する。
     */
    establishSecretViaHandshake: z.boolean().optional(),
    /**
     * ワークスペース設定の WebHook 定義（desired）への backlink。
     * `automationConfig.webhookEndpoints[].id` と対応し、突合（reconcile）で実体と定義を冪等に紐付ける。
     */
    workspaceDefinitionId: z.string().optional(),
    /**
     * 呼び出し側が指定する署名シークレット/トークンの平文（配布物のインストール時にユーザー再入力した値）。
     * 未指定時はサーバーが新規生成する。HMAC/token 方式でのみ意味を持ち、応答以外で平文を保持・再表示しない。
     */
    secret: z.string().optional(),
    /**
     * 作成時の初期有効状態（既定 true=enabled）。false で disabled として作成する。
     * 無効指定の定義を「一旦 enabled で作成→別呼び出しで無効化」する隙間（公開受信可能な瞬間）を
     * 作らないよう、作成時に状態を確定させる。
     */
    enabled: z.boolean().optional(),
});
/** エンドポイント更新入力（全フィールド任意の部分更新） */
export const UpdateEndpointInputSchema = z.object({
    name: z.string().min(1).optional(),
    workspaceId: z.string().min(1).optional(),
    promptTemplate: z.string().min(1).optional(),
    authType: EndpointAuthSchema.optional(),
    queueControl: QueueControlPatchSchema.optional(),
    /** タスク実行に用いる LLM モデル。null でワークスペース優先モデルに戻す。 */
    selectedModel: ModelSelectionSchema.nullable().optional(),
    /** ワークスペース設定 WebHook 定義への backlink（突合用） */
    workspaceDefinitionId: z.string().optional(),
});
// ========================================
// エンドポイントビュー（API レスポンス）
// ========================================
/**
 * エンドポイントの表示用ビュー。資格情報の平文は含めない。
 * receiveUrl は公開受信 URL、deliveryCount/lastDeliveredAt は受信統計（要件 1.8）。
 */
export const WebhookEndpointViewSchema = z.object({
    endpointId: z.string(),
    name: z.string(),
    receiveUrl: z.string(),
    workspaceId: z.string(),
    promptTemplate: z.string(),
    authType: EndpointAuthSchema,
    status: EndpointStatusSchema,
    queueControl: QueueControlSchema,
    selectedModel: ModelSelectionSchema.nullable(),
    createdAt: z.string(),
    lastDeliveredAt: z.string().nullable(),
    deliveryCount: z.number().int().nonnegative(),
    /** ワークスペース設定 WebHook 定義への backlink（突合用。未紐付けは null/未設定） */
    workspaceDefinitionId: z.string().nullable().optional(),
});
// ========================================
// 保留トリガー
// ========================================
/** 保留トリガーの状態 */
export const PendingTriggerStatusSchema = z.enum(['pending', 'consumed']);
/**
 * 消費理由（要件 5.7）。
 * - executed: 実行して消費
 * - expired: 実行有効期限超過によりスキップ消費
 * - superseded: 最新優先ポリシーで非最新のためスキップ消費
 */
export const ConsumeReasonSchema = z.enum(['executed', 'expired', 'superseded']);
/**
 * 保留トリガーの表示用ビュー（デスクトップが取得する pull 契約）。
 * 実行に必要な最小情報のみを公開し、内部状態（status/dedupeKey 等）は含めない。
 */
export const PendingTriggerViewSchema = z.object({
    triggerId: z.string(),
    endpointId: z.string(),
    workspaceId: z.string(),
    promptTemplate: z.string(),
    payload: z.unknown(),
    rawBody: z.string(),
    deliveredAt: z.string(),
});
/**
 * 保留トリガーの内部レコード（クラウド永続化の論理表現）。
 * テンプレートはスナップショットとして非正規化保持し、状態・消費理由・受信日時を持つ。
 */
export const PendingTriggerSchema = z.object({
    triggerId: z.string(),
    endpointId: z.string(),
    workspaceId: z.string(),
    ownerEmail: z.string(),
    promptTemplateSnapshot: z.string(),
    payload: z.unknown(),
    rawBody: z.string(),
    status: PendingTriggerStatusSchema,
    /** 送信元由来の冪等性キー（任意） */
    dedupeKey: z.string().optional(),
    /** 消費理由（消費済み時のみ） */
    consumeReason: ConsumeReasonSchema.optional(),
    deliveredAt: z.string(),
    consumedAt: z.string().optional(),
});
// ========================================
// タスク起源メタデータ
// ========================================
/**
 * WebHook 起因タスクの起源メタデータ。
 * TaskStartRequest.options（scheduleMetadata と同型）・履歴レコードへ伝播し、
 * 履歴フィルタ・通知に使用する（要件 10.1）。
 */
export const WebhookMetadataSchema = z.object({
    endpointId: z.string(),
    endpointName: z.string(),
    triggerId: z.string(),
    deliveredAt: z.string(),
});
// ========================================
// API レスポンス / リクエスト
// ========================================
/** 作成・再生成レスポンス（平文 secret は初回のみ含む） */
export const WebhookEndpointWithSecretSchema = z.object({
    endpoint: WebhookEndpointViewSchema,
    secret: z.string(),
});
/** エンドポイント一覧レスポンス */
export const WebhookEndpointListResponseSchema = z.object({
    endpoints: z.array(WebhookEndpointViewSchema),
});
/** PATCH /webhook-endpoints/{id} の本文（状態変更 or シークレット再生成） */
export const PatchEndpointRequestSchema = z.union([
    z.object({ status: EndpointStatusSchema }),
    z.object({ regenerateSecret: z.literal(true) }),
]);
/** 受信受理レスポンス */
export const WebhookAcceptedResponseSchema = z.object({
    accepted: z.literal(true),
    triggerId: z.string().nullable(),
});
/** 保留トリガー一覧レスポンス */
export const PendingTriggerListResponseSchema = z.object({
    triggers: z.array(PendingTriggerViewSchema),
    nextToken: z.string().optional(),
});
/** PATCH /pending-triggers/{id} の本文（消費通知） */
export const ConsumeTriggerRequestSchema = z.object({
    deliveredAt: z.string(),
    reason: ConsumeReasonSchema.optional(),
});
//# sourceMappingURL=webhook-schemas.js.map